import {
  Checkbox,
  ChoiceGroup,
  ComboBox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  DropdownMenuItemType,
  IComboBox,
  IComboBoxOption,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
} from '@fluentui/react';
import _ from 'lodash';
import moment from 'moment-timezone';
import * as React from 'react';
import BlockUi from 'react-block-ui';
import * as ReactDOM from 'react-dom';
import { connect, Provider as ProviderContext } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Market, PatientPreferredDoctor, ProviderData } from '../../../api';
import { HealAPI } from '../../../healApi';
import { AsyncData, onPageDidMount, onPageWillUnmount } from '../../../redux/core';
import { toast } from '../../../redux/message/actions';
import { ToastParams } from '../../../redux/message/types';
import { fetchAssistants, fetchDoctors } from '../../../redux/providers/actions';
import { store } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { fetchTimeslots } from '../../../redux/visits/actions';
import { TimeSlot, Visit } from '../../../redux/visits/types';
import { TimePicker } from '../../core/TimePicker';

export interface OwnProps {
  visit: Visit;
  preferredDoctor?: PatientPreferredDoctor;
  onSuccess: (visit: Visit) => void;
  onCancel?: () => void;
}
interface StateProps {
  doctors: AsyncData<ProviderData[]>;
  assistants: AsyncData<ProviderData[]>;
  markets: AsyncData<Market[]>;
  availableTimeSlots: AsyncData<TimeSlot[]>;
}
interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    toast: (params: ToastParams) => void;
    onPageWillUnmount: (name: string) => void;
    fetchDoctors: (serviceCode: string, marketCode: string, day?: number) => void;
    fetchAssistants: (serviceCode: string, marketCode: string, day?: number) => void;
    fetchTimeslots: (visitCode: string) => void;
  };
}

export type ChangeProviderDialogProps = OwnProps & StateProps & DispatchProps;

export interface ChangeProviderDialogState {
  date?: Date;
  doctorId?: string;
  assistantId?: string;
  timeSlot?: TimeSlot;
  timeSlotId: string;
  manualTime?: number;
  usingTimeSlot: boolean;
  showOnlyAvailableProviders: boolean;
  patientAffected: boolean;
  isSaving: boolean;
}

export class ChangeProviderDialog {
  public static show(options: OwnProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(
      <ProviderContext store={store}>
        <ChangeProviderDialogInternal {...options} close={close} />
      </ProviderContext>,
      dialogNode
    );
  }
}

class Component extends React.Component<ChangeProviderDialogProps & { close: () => void }, ChangeProviderDialogState> {
  private fetchProviders: () => void;

  constructor(props: any) {
    super(props);
    const { visit } = this.props;
    this.state = {
      doctorId: visit.doctorId || '',
      assistantId: visit.assistantId || '',
      timeSlotId: visit.timeSlotId,
      usingTimeSlot: true,
      showOnlyAvailableProviders: true,
      manualTime: moment(this.getDefaultTime()).tz(this.getMarketTimeZone()).unix() * 1000,
      patientAffected: false,
      isSaving: false,
    };
    this.fetchProviders = _.debounce(this.fetchProvidersDebounced, 350);
  }

  public componentDidMount() {
    // Setting global moment timezone to market timezone for DateTime component to use when handling manual input.
    moment.tz.setDefault(this.getMarketTimeZone());
    this.props.actions.onPageDidMount('ChangeProviderDialog');
    this.props.actions.fetchTimeslots(this.props.visit.visitCode);
    this.fetchProvidersDebounced();
  }

  public componentWillUnmount() {
    // Re-setting global moment timezone value to local when closing the dialog so it doesn't affect other visits.
    moment.tz.setDefault();
    this.props.actions.onPageWillUnmount('ChangeProviderDialog');
  }

  public render(): JSX.Element {
    const doctorOptions = this.providersForDropdown(this.props.doctors.data);
    const assistantOptions = this.assistantsForDropdown(this.props.assistants.data);
    return (
      <Dialog
        hidden={false}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Update Provider and Time for visit: ' + this.props.visit.visitCode,
          subText: this.props.visit.timezone,
        }}
        onDismiss={() => this.cancel()}
        minWidth={550}
        modalProps={{
          isBlocking: true,
        }}
      >
        <BlockUi tag="div" blocking={this.state.isSaving}>
          <Stack tokens={{ childrenGap: 12 }}>
            <ChoiceGroup
              className="inline-flex"
              defaultSelectedKey="A"
              options={[
                { key: 'A', text: 'Time slot' },
                { key: 'B', text: 'Manual time' },
              ]}
              onChange={this.toggleUsingTimeSlot}
            />

            {this.state.usingTimeSlot && (
              <>
                <Dropdown
                  label="Select time slot"
                  selectedKey={this.state.timeSlotId}
                  onRenderItem={(props, defaultRender) => {
                    return <div style={{ backgroundColor: (props as any)?.available ? 'transparent' : '#96000032' }}>{defaultRender!(props)}</div>;
                  }}
                  options={this.timeSlotsForDropdown(this.props.availableTimeSlots.data)}
                  onChange={this.onTimeSlotChange}
                  required={true}
                />
                {this.state.timeSlot && !this.state.timeSlot.available && (
                  <MessageBar messageBarType={MessageBarType.severeWarning}>
                    This selected slot is currently unavailable. You may not be able to update without permissions.
                  </MessageBar>
                )}
              </>
            )}

            {!this.state.usingTimeSlot && (
              <TimePicker
                label="Enter Manual Time"
                value={moment(this.state.manualTime).tz(this.props.visit.timezone)}
                onChange={(m) => this.setState({ manualTime: m.unix() * 1000 })}
              />
            )}

            <Checkbox
              label={'Only show Doctors and MAs available on the selected day'}
              checked={this.state.showOnlyAvailableProviders}
              onChange={this.onChangeOnlyAvailableProviders}
            />

            <ComboBox
              label="Select provider"
              allowFreeform={true}
              selectedKey={this.state.doctorId}
              options={doctorOptions}
              onChange={this.onDoctorChanged}
              disabled={this.props.doctors.isFetching}
              required={!!this.state.assistantId && this.props.visit.visitServiceCode !== 'AVATAR_TM'}
            />
            {!doctorOptions.find((o) => o.key === this.state.doctorId) && (
              <MessageBar messageBarType={MessageBarType.severeWarning}>
                The selected doctor could not be found in the allowed list (they may not have a shift for this market on the date of service).
              </MessageBar>
            )}

            <ComboBox
              label="Select medical assistant (MA)"
              allowFreeform={true}
              selectedKey={this.state.assistantId}
              options={assistantOptions}
              onChange={this.onAssistantChange}
              disabled={this.props.assistants.isFetching}
              required={this.props.visit.visitServiceCode === 'AVATAR_TM'}
            />
            {!assistantOptions.find((o) => o.key === this.state.assistantId) && (
              <MessageBar messageBarType={MessageBarType.severeWarning}>
                The selected assistant could not be found in the allowed list (they may not have a shift for this market on the date of service).
              </MessageBar>
            )}

            <Checkbox label="Patient consented to reschedule" onChange={this.onChangePatientConsented} />
            <DialogFooter>
              <PrimaryButton onClick={this.onChangeProvider} disabled={!this.isValid()}>
                Ok
              </PrimaryButton>
              <DefaultButton onClick={() => this.cancel()}>Cancel</DefaultButton>
            </DialogFooter>
          </Stack>
        </BlockUi>
      </Dialog>
    );
  }

  private onChangeOnlyAvailableProviders = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    this.setState({ showOnlyAvailableProviders: checked! }, this.fetchProviders);
  };

  private onChangePatientConsented = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    this.setState({ patientAffected: checked! });
  };

  private fetchProvidersDebounced() {
    const day = this.state.showOnlyAvailableProviders ? this.state.manualTime : undefined;
    this.props.actions.fetchDoctors(this.props.visit.visitServiceCode, this.props.visit.marketCode, day);
    this.props.actions.fetchAssistants(this.props.visit.visitServiceCode, this.props.visit.marketCode, day);
  }

  private getProviderList(providersType: string, providerTypeShort: string, providers?: ProviderData[]): IDropdownOption[] {
    const { visit, preferredDoctor } = this.props;
    let options: IDropdownOption[] = [];
    if (providers) {
      /**
       * If the currently selected provider for the visit is no longer active, remove them from
       * the list so that the visit must be assigned to another provider. This can occur when the
       * visit is booked a long ways in advance and the provider terminates their relationship
       * with Heal in the time between scheduling and the date of service. Because we generate this
       * list option using the first and last name of the provider returned in the visit data, we
       * need to explicitly search the provider list for their ID and ensure they're active.
       * @see [APP-9189](https://jira.heal.com/browse/APP-9189)
       */
      const visitProviderIsActive = providers.find(provider => provider.doctorId === visit.selectedDoctorId)?.active;
      if (providersType === 'DOCTOR' && visit.selectedDoctorId && visitProviderIsActive) {
        options.push({
          key: 'Header-Selected-Doctor',
          text: 'Selected Doctor',
          itemType: DropdownMenuItemType.Header,
        });
        options = options.concat({
          key: visit.selectedDoctorId,
          text: visit.selectedDoctorFirstName + ' ' + visit.selectedDoctorLastName,
        });
      }
      if (providersType === 'DOCTOR' && preferredDoctor && preferredDoctor.stillValid) {
        options.push({
          key: 'Header-Preferred-Doctor',
          text: 'Preferred Doctor',
          itemType: DropdownMenuItemType.Header,
        });
        options = options.concat({
          key: preferredDoctor.doctor.providerId,
          text: preferredDoctor.doctor.firstName + ' ' + preferredDoctor.doctor.lastName,
        });
      }
      if (this.state.showOnlyAvailableProviders) {
        options.push({
          key: 'Header-Market',
          text: providerTypeShort + 's for ' + visit.marketCode,
          itemType: DropdownMenuItemType.Header,
        });
        const inMarketProviders = providers
          .filter((p) => p.active && p.providerType === providersType && p.providerMarkets.some((m) => m.marketCode === visit.marketCode))
          .map((p) => ({ key: p.id, text: p.firstName + ' ' + p.lastName }));
        options = options.concat(inMarketProviders);

        options.push({
          key: 'Header-Other',
          text: 'Other ' + providerTypeShort + 's',
          itemType: DropdownMenuItemType.Header,
        });
        const outMarketProviders = providers
          .filter((p) => p.active && p.providerType === providersType && p.providerMarkets.every((m) => m.marketCode !== visit.marketCode))
          .map((p) => ({ key: p.id, text: p.firstName + ' ' + p.lastName }));
        options = options.concat(outMarketProviders);
      } else {
        options.push({
          key: 'Header-Other',
          text: 'Other ' + providerTypeShort + 's',
          itemType: DropdownMenuItemType.Header,
        });
        const allProviders = providers.filter((p) => p.active && p.providerType === providersType).map((p) => ({ key: p.id, text: p.firstName + ' ' + p.lastName }));
        options = options.concat(allProviders);
      }
    }

    return options;
  }

  private providersForDropdown(providers?: ProviderData[]): IDropdownOption[] {
    const options: IDropdownOption[] = [{ key: '', text: 'No Doctor' }];
    return options.concat(this.getProviderList('DOCTOR', 'Doctor', providers));
  }

  private assistantsForDropdown(providers?: ProviderData[]): IDropdownOption[] {
    const options: IDropdownOption[] = [{ key: '', text: 'No Assistant' }];
    return options.concat(this.getProviderList('MEDICAL_ASSISTANT', 'MAs', providers));
  }

  private getDefaultTime(): number {
    let defaultTime = this.props.visit.timeslotStartTime;
    if (this.props.visit.estimatedStartTime > 0) {
      defaultTime = this.props.visit.scheduledTime;
    }
    return defaultTime;
  }

  private onDoctorChanged = (event: React.FormEvent<IComboBox>, option?: IComboBoxOption): void => {
    this.setState({ doctorId: option!.key as string });
  };

  private onAssistantChange = (event: React.FormEvent<IComboBox>, option?: IDropdownOption) => {
    this.setState({ assistantId: option!.key as string });
  };

  private timeSlotsForDropdown(timeSlots?: TimeSlot[]): IDropdownOption[] {
    if (!timeSlots || timeSlots.length === 0) {
      return [];
    }
    const newTimeslots = timeSlots.slice(0, Math.min(timeSlots.length, 500)).map((timeSlot) => {
      const marketTimeZone = this.getMarketTimeZone();
      const startTime = moment(timeSlot.startTime).tz(marketTimeZone);
      const endTime = moment(timeSlot.endTime).tz(marketTimeZone);
      const textTime = `${startTime.format('h:mm A')} - ${endTime.format('h:mm A z')}`;

      return {
        key: timeSlot.id,
        text: timeSlot.longDateString + ' ' + textTime,
        available: timeSlot.available,
      };
    });

    if (!newTimeslots.find((t) => t.key === this.state.timeSlotId)) {
      const { timeslotStartTime, timeslotEndTime } = this.props.visit;
      const marketTimeZone = this.getMarketTimeZone();
      const startTime = moment(timeslotStartTime).tz(marketTimeZone);
      const endTime = moment(timeslotEndTime).tz(marketTimeZone);
      const textTime = `${startTime.format('h:mm A')} - ${endTime.format('h:mm A z')}`;

      newTimeslots.unshift({
        key: this.state.timeSlotId,
        text: startTime.isBefore(moment().tz(marketTimeZone)) ? "The visit's timeslot has already passed" : this.props.visit.longDateString + ' ' + textTime,
        available: true,
      });
    }
    return newTimeslots;
  }

  private getMarketTimeZone(): string {
    return this.props.visit.timezone;
  }

  private onTimeSlotChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
    if (option) {
      const timeSlot = index !== undefined ? this.props.availableTimeSlots.data![index] : undefined;
      const manualTime = timeSlot ? timeSlot.startTime : undefined;
      this.setState(
        {
          usingTimeSlot: true,
          timeSlotId: option.key as string,
          timeSlot,
          manualTime,
        },
        this.fetchProviders
      );
    }
  };

  private toggleUsingTimeSlot = () => {
    this.setState({ usingTimeSlot: !this.state.usingTimeSlot });
  };

  private isValid() {
    if (this.props.visit.visitServiceCode === 'AVATAR_TM') {
      return !!this.state.assistantId;
    } else {
      if (this.state.assistantId && !this.state.doctorId) {
        return false;
      }
    }

    return true;
  }

  private cancel() {
    this.props.onCancel && this.props.onCancel();
    this.props.close();
  }

  private onChangeProvider = async () => {
    if (this.isValid()) {
      this.setState({ isSaving: true });
      const changeProviderResponse = await HealAPI.changeProvider(
        this.props.visit.visitCode,
        false,
        this.state.patientAffected,
        this.state.doctorId,
        this.state.assistantId,
        this.state.usingTimeSlot ? this.state.timeSlotId : undefined,
        !this.state.usingTimeSlot ? this.state.manualTime : undefined
      );

      const visit = changeProviderResponse.data;

      if (!changeProviderResponse.ok || !visit) {
        this.setState({ isSaving: false });
        this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: (changeProviderResponse.data as any)?.['description'] || 'An Error occurred, please try again later',
        });
        this.setState({ isSaving: false });
        return;
      }

      const timeSlotChanged = this.props.visit.timeSlotId !== visit.timeSlotId;
      const providerRemoved = !!this.props.visit.doctorId && !visit.doctorId;
      const providerChangedToAnotherProvider = !!this.props.visit.doctorId && !!visit.doctorId && this.props.visit.doctorId !== visit.doctorId;
      const providerAdded = !this.props.visit.doctorId && !!visit.doctorId;

      let successMessage = '';

      switch (true) {
        case timeSlotChanged && (providerChangedToAnotherProvider || providerAdded):
          successMessage = 'Successfully updated time for visit ' + visit.visitCode + ' and provider to ' + visit.doctorName;
          break;
        case providerChangedToAnotherProvider || providerAdded:
          successMessage = 'Successfully updated provider to ' + visit.doctorName;
          break;
        case timeSlotChanged && providerRemoved:
          successMessage = 'Successfully updated time for visit ' + visit.visitCode + ' and removed provider.';
          break;
        case providerRemoved:
          successMessage = 'Successfully removed provider.';
          break;
        default:
          successMessage = 'Successfully updated time for visit ' + visit.visitCode;
          break;
      }

      this.props.actions.toast({
        type: 'success',
        title: 'Success',
        content: successMessage,
      });

      // changeProvider API returns a different Visit object from what we want to store in Redux.
      const fetchVisitResponse = await HealAPI.fetchVisit(visit.visitCode);
      const fetchedVisit = fetchVisitResponse.data;

      if (!fetchVisitResponse.ok || !fetchedVisit) {
        this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: 'There was a problem fetching and displaying the visit. If you do not see your changes please refresh the page.',
        });
        this.cancel();
        return;
      }

      this.props.onSuccess(fetchedVisit);
      this.props.close();
    }
  };
}

export function mapStateToProps(state: RootState): StateProps {
  return {
    doctors: state.providers.doctors,
    assistants: state.providers.assistants,
    availableTimeSlots: state.visits.availableTimeSlots,
    markets: state.markets.markets,
  };
}

export function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    actions: bindActionCreators({ toast, onPageDidMount, onPageWillUnmount, fetchDoctors, fetchAssistants, fetchTimeslots }, dispatch),
  };
}

export const ChangeProviderDialogInternal = connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component);
